// UserDetails.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from './BottomNav';
import MoreButton from './MoreButton';
import AppDrawer from './AppDrawer';
import FeedbackLink from './FeedbackLink';
import DesktopNav from './DesktopNav';
import { AlertBanner } from '../components/AlertBanner';
import './UserDetails.css';

// Back arrow icon component
const BackArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

const UserDetails = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, refreshUser, moodleData } = useAuth();
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  // Run once on component mount
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        // Start with existing data
        if (user) {
          setLoading(false);
        }
        
        // Try to refresh user data
        await refreshUser();
        
        // Only update state if component is still mounted
        if (isMounted) {
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading user data:", err);
        if (isMounted) {
          setError("Could not load user information");
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    // Cleanup
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - run once on mount only

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  // Get the best available user information
  const getUserInfo = () => {
    // Start with our basic user data
    let userInfo = {
      displayName: '',
      firstName: '',
      lastName: '',
      email: '',
      viterboId: '',
      accountType: ''
    };
    
    // Add data from the user state
    if (user) {
      userInfo = {
        ...userInfo,
        ...user
      };
      
      // Try to split displayName into first and last name if not already available
      if (!userInfo.firstName && userInfo.displayName) {
        const nameParts = userInfo.displayName.split(' ');
        if (nameParts.length >= 2) {
          userInfo.firstName = nameParts[0];
          userInfo.lastName = nameParts.slice(1).join(' ');
        } else {
          userInfo.firstName = userInfo.displayName;
        }
      }
    }
    
    // Add Moodle data if available
    if (moodleData && moodleData.user) {
      const mUser = moodleData.user;
      
      // Update with Moodle data if not already set
      userInfo = {
        ...userInfo,
        displayName: userInfo.displayName || mUser.fullname,
        email: userInfo.email || mUser.email,
        viterboId: mUser.idnumber,
        profileImage: mUser.profileimageurl
      };
      
      // Try to get first/last name from Moodle data if not already set
      if ((!userInfo.firstName || !userInfo.lastName) && mUser.fullname) {
        const nameParts = mUser.fullname.split(' ');
        if (nameParts.length >= 2) {
          userInfo.firstName = userInfo.firstName || nameParts[0];
          userInfo.lastName = userInfo.lastName || nameParts.slice(1).join(' ');
        } else {
          userInfo.firstName = userInfo.firstName || mUser.fullname;
        }
      }
      
      // Use specific Moodle fields if available
      if (mUser.firstname) {
        userInfo.firstName = userInfo.firstName || mUser.firstname;
      }
      
      if (mUser.lastname) {
        userInfo.lastName = userInfo.lastName || mUser.lastname;
      }
    }
    
    // Determine account type
    userInfo.accountType = process.env.NODE_ENV === 'development' && userInfo.developerAccess 
      ? 'Development Account' 
      : 'Student Account';
    
    return userInfo;
  };

  const userInfo = getUserInfo();

  if (loading) {
    return (
      <div className="dashboard user-details-page">
        <header className="dashboard-header">
          <div className="dashboard-brand">
            <button 
              className="back-button" 
              onClick={handleBackClick}
              aria-label="Go back to dashboard"
            >
              <BackArrowIcon />
            </button>
            <h1>User Details</h1>
          </div>
          <div className="dashboard-header-right">
            <MoreButton onClick={() => setDrawerOpen(true)} />
          </div>
        </header>
        <AlertBanner />
        <DesktopNav />
        <div className="dashboard-content" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner"></div>
            <p>Loading user details...</p>
          </div>
        </div>
        <AppDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} onOpen={() => setDrawerOpen(true)} />
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="dashboard user-details-page">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <button
            className="back-button"
            onClick={handleBackClick}
            aria-label="Go back to dashboard"
          >
            <BackArrowIcon />
          </button>
          <h1>User Details</h1>
        </div>
        <div className="dashboard-header-right">
          <MoreButton onClick={() => setDrawerOpen(true)} />
        </div>
      </header>
      <AlertBanner />
      <DesktopNav />
      <div className="dashboard-content">
        <div className="dashboard-card">
          <div className="user-details-inner">
            <div className="user-profile-image">
              <img
                src={userInfo.profileImage || `${process.env.PUBLIC_URL}/viterbo-icon-192x192.png`}
                alt={`${userInfo.displayName || 'User'}'s profile`}
                onError={e => { e.currentTarget.src = `${process.env.PUBLIC_URL}/viterbo-icon-192x192.png`; }}
              />
            </div>

            <div className="user-details-grid">
              {userInfo.firstName && (
                <div className="user-details-item">
                  <span className="user-details-label">First Name</span>
                  <span className="user-details-value">{userInfo.firstName}</span>
                </div>
              )}

              {userInfo.lastName && (
                <div className="user-details-item">
                  <span className="user-details-label">Last Name</span>
                  <span className="user-details-value">{userInfo.lastName}</span>
                </div>
              )}

              {userInfo.email && (
                <div className="user-details-item">
                  <span className="user-details-label">Email</span>
                  <span className="user-details-value">{userInfo.email}</span>
                </div>
              )}

              {userInfo.viterboId && (
                <div className="user-details-item">
                  <span className="user-details-label">Viterbo ID</span>
                  <span className="user-details-value">{userInfo.viterboId}</span>
                </div>
              )}
            </div>

            {error && (
              <div className="error-message" style={{
                backgroundColor: "rgba(158, 27, 52, 0.1)",
                color: "#9E1B34",
                padding: "12px",
                borderRadius: "8px",
                margin: "16px 0"
              }}>
                {error}
              </div>
            )}

            <div className="user-details-actions">
              <button className="btn-sso" onClick={handleGoBack}>
                Back to Dashboard
              </button>
            </div>

            <FeedbackLink text="Profile info look wrong? Let us know" />
          </div>
        </div>
      </div>
      <AppDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} onOpen={() => setDrawerOpen(true)} />
      <BottomNav />
    </div>
  );
};

export default UserDetails;